import { ActionType } from '../../core/redux/actionType';
import { ApiReducer, makeApiAction, makeApiReducer, makeRsaaTypes } from '../../core/api/rsaa';
import { addError, addLoading, noLoadingNoError, getLoading } from '../../core/api/module';
import * as R from 'ramda';
import { Endpoints } from '../../core/api/endpoints';
import { AppState } from '../../core/redux/appState';
import { ReportRecipientParticipant } from '../models';


const KEY_ACTION = ActionType.ReportRecipientParticipantsRequest;
const ACTIONS    = [
  ActionType.ReportRecipientParticipantsRequest,
  ActionType.ReportRecipientParticipantsResponse,
  ActionType.ReportRecipientParticipantsError,
];


type Payload = ReportRecipientParticipant[];


type Meta = {
  recipientId: number;
  projectIds?: number[];
  dateRange?: number;
};


/**
 * Dispatch this to retrieve all the participants for whom the recipient can access reports
 *
 * @param recipientId - the user's database person Id
 * @param projectIds - can be undefined or a list of project ids - defaults to empty list
 * @param dateRange - can be undefined or the selected date range - defaults to 3 (last 3 months)
  */
export const reportRecipientParticipantsAction = (
  recipientId: number,
  projectIds?: number[],
  dateRange?: number,
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.ReportRecipientParticipants,
    headers:  { 'content-type': 'application/json' },
    body:     JSON.stringify({
      recipientId: recipientId,
      projectIds: projectIds,
      dateRange: dateRange,
    }),
    types:    makeRsaaTypes<Payload, Meta>(ACTIONS, { recipientId, projectIds, dateRange }),
    bailout:  (state: AppState) => !!getLoading(state, KEY_ACTION, `${recipientId}`),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const metaTag = `${action.meta.recipientId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  return R.assoc('reportRecipientParticipants', payload, successState);
};

export const reportRecipientParticipantsReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
